import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Download, Check, ArrowRight, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandHeader from "@/components/BrandHeader";
import BrandFooter from "@/components/BrandFooter";
import { track, identifyUser, EVENTS } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 30; // ~75 seconds — Stripe can take a moment on slow networks

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | expired | error | unknown
  const [order, setOrder] = useState(null);
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("unknown"); return; }

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        setOrder(res.data);
        if (res.data.payment_status === "paid") {
          if (!trackedRef.current) {
            trackedRef.current = true;
            if (res.data.email) identifyUser(res.data.email, { source: "customer" });
            track(EVENTS.PURCHASE_COMPLETED, {
              product_id: res.data.product_id,
              product_title: res.data.product_title,
              amount_total: res.data.amount_total,
              currency: res.data.currency || "usd",
              session_id: sessionId,
            });
          }
          setState("paid");
          return;
        }
        if (res.data.status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("error");
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("error");
        } else {
          timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };

    poll();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white text-[#333333]" data-testid="checkout-success-page">
      <BrandHeader variant="cream" />

      <section className="py-24 md:py-32">
        <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
          {state === "polling" && (
            <div data-testid="checkout-polling">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#F8F6F2]">
                <Loader2 className="h-6 w-6 text-[#8FAF8A] animate-spin" />
              </div>
              <h1 className="mt-6 font-serif-display text-4xl md:text-5xl font-medium text-[#333333]">
                Confirming your payment<span className="italic">…</span>
              </h1>
              <p className="mt-4 text-[#4A4A4A]">Sit tight — this usually takes just a moment.</p>
            </div>
          )}

          {state === "paid" && order && (
            <div data-testid="checkout-paid">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#8FAF8A]">
                <Check className="h-6 w-6 text-white" />
              </div>
              <p className="mt-6 text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A]">Order complete</p>
              <h1 className="mt-3 font-serif-display text-4xl md:text-5xl font-medium text-[#333333] leading-tight">
                Thank you. <span className="italic">The work begins.</span>
              </h1>
              <p className="mt-5 text-lg text-[#4A4A4A]">
                Your copy of <strong className="text-[#333333]">{order.product_title}</strong> is ready. A receipt with your download link is also on its way to your inbox.
              </p>
              <div className="mt-8 rounded-3xl border border-[#EDE9E1] bg-[#F8F6F2] p-8 text-left" data-testid="download-card">
                <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A]">Your download</div>
                <div className="mt-2 font-display font-bold text-lg text-[#333333]">{order.product_title}</div>
                <a
                  href={`${API}/orders/${sessionId}/download`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#333333] hover:bg-black text-white h-12 px-6 text-sm font-semibold transition-all"
                  data-testid="download-purchased-pdf"
                >
                  <Download className="h-4 w-4" /> Download your PDF
                </a>
                <p className="mt-4 text-xs text-[#8A8A8A]">
                  Keep this link private — it&apos;s tied to your order and gives instant access to the PDF.
                </p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <Button asChild variant="outline" className="rounded-full bg-white border-[#EDE9E1] text-[#333333] hover:bg-[#F8F6F2] h-12 px-6 font-semibold" data-testid="continue-shop">
                  <Link to="/shop">Continue shopping <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="ghost" className="rounded-full h-12 px-6 text-[#333333] hover:bg-[#F8F6F2]" data-testid="back-home">
                  <Link to="/">Back to home</Link>
                </Button>
              </div>
            </div>
          )}

          {state === "expired" && (
            <div data-testid="checkout-expired">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#F8F6F2]">
                <XCircle className="h-6 w-6 text-[#B8860B]" />
              </div>
              <h1 className="mt-6 font-serif-display text-4xl md:text-5xl font-medium text-[#333333]">Session expired.</h1>
              <p className="mt-4 text-[#4A4A4A]">Your checkout session timed out. No charge was made. You can try again anytime.</p>
              <Button asChild className="mt-8 rounded-full bg-[#333333] hover:bg-black text-white h-12 px-6 font-semibold" data-testid="try-again">
                <Link to="/shop">Back to shop <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          )}

          {state === "error" && (
            <div data-testid="checkout-error">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#F8F6F2]">
                <XCircle className="h-6 w-6 text-[#4A4A4A]" />
              </div>
              <h1 className="mt-6 font-serif-display text-4xl md:text-5xl font-medium text-[#333333]">Still waiting on Stripe.</h1>
              <p className="mt-4 text-[#4A4A4A]">
                We&apos;re taking a bit longer than expected to confirm this payment. Check your email — if your receipt arrives, your download is ready. Otherwise, please write to us.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <a href={`mailto:support@wishingwellnesslife.com?subject=Order%20status%20-%20${sessionId}`} className="rounded-full bg-[#333333] hover:bg-black text-white h-12 px-6 font-semibold inline-flex items-center justify-center" data-testid="contact-support">
                  Email support
                </a>
                <Button asChild variant="outline" className="rounded-full bg-white border-[#EDE9E1] text-[#333333] hover:bg-[#F8F6F2] h-12 px-6 font-semibold">
                  <Link to="/shop">Back to shop</Link>
                </Button>
              </div>
            </div>
          )}

          {state === "unknown" && (
            <div data-testid="checkout-unknown">
              <h1 className="font-serif-display text-4xl md:text-5xl font-medium text-[#333333]">No session found.</h1>
              <p className="mt-4 text-[#4A4A4A]">This page confirms a purchase after Stripe checkout. Head back to the shop to browse.</p>
              <Button asChild className="mt-8 rounded-full bg-[#333333] hover:bg-black text-white h-12 px-6 font-semibold">
                <Link to="/shop">Back to shop <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      <BrandFooter />
    </div>
  );
}
